import type {PinterestPinStatus, PinterestProfileStatus} from '@/api/pinterest';

type Field = {FieldName: string; Content: string | boolean};

function pushCount(fields: Field[], name: string, value: number | null | undefined) {
  if (value !== null && value !== undefined) {
    fields.push({FieldName: name, Content: String(value)});
  }
}

/**
 The `Fields[]` a profile looked up live is built with, under the FieldNames a crawled Pinterest
 profile carries, so its card shows followers, verification and bio like a scraped one. Counts are
 strings and flags real booleans, as Scrapy writes them. Absent values are omitted, never sent as 0
 or false: absent means unknown.
 */
export function buildPinterestProfileMetadataFields(profile: PinterestProfileStatus): Field[] {
  const fields: Field[] = [
    {FieldName: 'Platform', Content: 'Pinterest'},
    {FieldName: 'Author Handle', Content: profile.username}
  ];
  pushCount(fields, 'Followers Count', profile.follower_count);
  pushCount(fields, 'Following Count', profile.following_count);
  pushCount(fields, 'Pin Count', profile.pin_count);
  pushCount(fields, 'Board Count', profile.board_count);
  if (profile.is_verified_merchant !== null && profile.is_verified_merchant !== undefined) {
    fields.push({FieldName: 'Is Verified Merchant', Content: profile.is_verified_merchant});
  }

  if (profile.bio) {
    fields.push({FieldName: 'Bio', Content: profile.bio});
  }

  if (profile.website_url) {
    fields.push({FieldName: 'Bio Link', Content: profile.website_url});
  }

  // A one-shot lookup that just succeeded can only honestly report "live".
  fields.push({FieldName: 'Live Status', Content: 'live'});
  return fields;
}

/** The pin counterpart: saves, link, domain and the video flag the lookup already holds. */
export function buildPinterestPinMetadataFields(pin: PinterestPinStatus): Field[] {
  const fields: Field[] = [
    {FieldName: 'Platform', Content: 'Pinterest'},
    {FieldName: 'Author Handle', Content: pin.owner.username}
  ];
  if (pin.description) {
    fields.push({FieldName: 'Description', Content: pin.description});
  }

  if (pin.image_url) {
    fields.push({FieldName: 'Image', Content: pin.image_url});
  }

  if (pin.domain) {
    fields.push({FieldName: 'Domain', Content: pin.domain});
  }

  if (pin.link) {
    fields.push({FieldName: 'Primary Attached Link', Content: pin.link});
  }

  pushCount(fields, 'Save Count', pin.save_count);
  fields.push(
    {FieldName: 'Contains Video', Content: pin.is_video},
    {FieldName: 'Live Status', Content: 'live'}
  );
  return fields;
}
